/**
 * Get a stable React `key` for a Show route renderer from the route `uuid`
 * and its URL query params. The `useQueryLoader` reference held inside a Show
 * route renderer survives for the lifetime of the mount, so a renderer that
 * stays mounted across same-`uuid` query-param navigation (e.g. date-range or
 * chart-view changes) would otherwise keep rendering the stale reference and
 * swallow a subsequent found-relay network error. Including the query params in
 * the mount key forces a fresh renderer whenever they change, disposing the
 * stale query and re-arming the error bridge.
 * @param {object} props - Found-relay route props (or a `{ match }` fallback).
 * @param {object} props.match - The matched route object.
 * @param {object} props.match.params - URL parameters from the matched route.
 * @param {string} props.match.params.uuid - The UUID from the route path.
 * @param {object} [props.match.location] - The matched location.
 * @param {object} [props.match.location.query] - Parsed URL query params.
 * @returns {string} A deterministic key combining the route `uuid` and a
 *   stable serialization of the query params.
 */
function getShowRouteKey(props) {
  const { match } = props || {};
  const routeUuid = match?.params?.uuid ?? '';
  const query = match?.location?.query || {};

  const serialisedQuery = Object.keys(query)
    .sort()
    .map((queryKey) => `${queryKey}=${query[queryKey]}`)
    .join('&');

  return `${routeUuid}|${serialisedQuery}`;
}

export default getShowRouteKey;
